import React, { useEffect, useState } from 'react';
import { Monitor, Apple } from 'lucide-react';

/**
 * AppDownloadCard — bietet die nativen Offline-Apps (Windows .exe / Mac .zip)
 * zum Download an. Prüft per HEAD, welche Dateien im aktuellen Environment
 * vorhanden sind und blendet fehlende Downloads aus.
 */
const DOWNLOADS = [
  {
    key: 'win',
    url: '/downloads/STIG-Rock-Setup.exe',
    label: 'Windows-App (.exe)',
    icon: Monitor,
    hint: 'Doppelklick installiert — Icon im Startmenü & Desktop',
  },
  {
    key: 'mac',
    url: '/downloads/STIG-Rock-Mac.zip',
    label: 'Mac-App (.zip)',
    icon: Apple,
    hint: 'Entpacken, App in „Programme" ziehen, Rechtsklick → Öffnen',
  },
];

const fmtMB = (bytes) => `${Math.round(bytes / (1024 * 1024))} MB`;

export default function AppDownloadCard() {
  const [available, setAvailable] = useState(null);

  useEffect(() => {
    let alive = true;
    Promise.all(
      DOWNLOADS.map(async (d) => {
        try {
          const res = await fetch(d.url, { method: 'HEAD' });
          if (!res.ok) return null;
          const size = parseInt(res.headers.get('content-length') || '0', 10);
          // Sanity guard: eine echte Offline-App ist mind. 20 MB (Chromium-Runtime).
          // Kleinere Dateien sind fehlgeschlagene/veraltete Build-Artefakte und
          // dürfen dem Presenter nicht als "downloadbar" angezeigt werden.
          if (size > 0 && size < 20 * 1024 * 1024) return null;
          return { ...d, size };
        } catch (_) {
          return null;
        }
      }),
    ).then((results) => {
      if (alive) setAvailable(results.filter(Boolean));
    });
    return () => { alive = false; };
  }, []);

  if (!available || available.length === 0) return null;

  return (
    <div className="pwa-install-block" data-testid="app-download-card">
      <div className="pwa-install-copy">
        <div className="pwa-install-kicker">DESKTOP-APP</div>
        <div className="pwa-install-title">Offline-App herunterladen</div>
        <p className="pwa-install-text">
          Die komplette Präsentation als eigenständiges Programm — alle Videos
          fest eingebaut, läuft zu 100% ohne Internet und ohne Browser.
          Datei kann beliebig an andere Presenter weitergegeben werden.
        </p>
      </div>
      <div className="pwa-install-actions">
        {available.map((d) => {
          const Icon = d.icon;
          return (
            <a
              key={d.key}
              href={d.url}
              download
              className="pwa-btn pwa-btn-primary"
              data-testid={`app-download-${d.key}`}
              title={d.hint}
            >
              <Icon size={14} strokeWidth={1.7} />
              <span>{d.label}{d.size > 0 ? ` · ${fmtMB(d.size)}` : ''}</span>
            </a>
          );
        })}
      </div>
      <div className="app-download-hints" data-testid="app-download-hints">
        {available.map((d) => (
          <div key={d.key} className="app-download-hint">
            <b>{d.label}:</b> {d.hint}
            {d.key === 'win' && ' — bei der Windows-Warnung „Weitere Informationen" → „Trotzdem ausführen" wählen.'}
          </div>
        ))}
      </div>
    </div>
  );
}
